/**
 * Fluxul clipului (spec-platforma-client §2, pipeline §3). Stări live pe WebSocket:
 * scenariu de aprobat → imagini de aprobat (aici se consumă 1 clip) → generare → preview
 * (watermark) → acceptare → livrat. Regenerări cu contor. Ultra: brief → editor → review.
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { ClipStatus, ScenarioSegment } from '@reclame-ai/shared';
import { useClip, useMutation, useInvalidate, qk } from '../api/hooks';
import {
  acceptClip,
  approveImages,
  approveScript,
  downloadClip,
  editSegment,
  regenerateStep,
  setPronunciation,
  submitUltraBrief,
} from '../api/endpoints';
import { humanError } from '../api/client';
import { useToast } from '../context/ToastContext';
import { categoryLabel, counterLabel, tierLabel } from '../lib/format';
import { CLIP_STATUS_LABEL, ClipFrame, ClipStatusBadge, isProcessing } from '../components/clip';
import { MediaImage, MediaVideo } from '../components/Media';
import { resolveMediaUrl } from '../lib/media';
import { PronunciationAssistant } from '../components/PronunciationAssistant';
import { ShareModal } from '../components/ShareModal';
import { ErrorBlock, Skeleton, Spinner } from '../components/ui';

const FLOW: { key: string; label: string; states: ClipStatus[] }[] = [
  { key: 'scenariu', label: 'Scenariu', states: ['scenariu_generare', 'scenariu_generat', 'scenariu_aprobat'] },
  { key: 'imagini', label: 'Imagini', states: ['imagini_generare', 'imagini_generate', 'imagini_aprobate'] },
  { key: 'generare', label: 'Generare', states: ['video_generare', 'audio', 'montaj'] },
  { key: 'preview', label: 'Preview', states: ['preview'] },
  { key: 'livrat', label: 'Livrat', states: ['livrat', 'ultra_briefing', 'ultra_editare', 'ultra_review'] },
];

export function ClipPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const invalidate = useInvalidate();
  const [shareOpen, setShareOpen] = useState(false);
  const [briefText, setBriefText] = useState('');

  const clipQ = useClip(id, false);
  const clip = clipQ.data?.clip;
  const processing = clip ? isProcessing(clip.status) : false;

  // Refetch periodic în timpul procesării (resync + fallback la WS).
  const clipLive = useClip(id, processing ? 2500 : false);
  const data = clipLive.data ?? clipQ.data;
  const c = data?.clip;

  useEffect(() => {
    document.title = c?.title ? `${c.title} — Reclame Video` : 'Clip — Reclame Video';
  }, [c?.title]);

  const approveScriptMut = useMutation({
    mutationFn: () => approveScript(id!),
    onSuccess: () => invalidate([qk.clip(id!), ['clips']]),
    onError: (e) => toast.push({ kind: 'err', title: 'Eroare', body: humanError(e) }),
  });
  const approveImagesMut = useMutation({
    mutationFn: () => approveImages(id!),
    onSuccess: () => {
      invalidate([qk.clip(id!), ['clips'], qk.balance()]);
      toast.push({ kind: 'ok', title: 'Imagini aprobate', body: 'Am consumat 1 clip din sold. Generăm videoclipul.' });
    },
    onError: (e) => toast.push({ kind: 'err', title: 'Eroare', body: humanError(e) }),
  });
  const regenMut = useMutation({
    mutationFn: (step: 'scenario' | 'images' | 'video') => regenerateStep(id!, step),
    onSuccess: () => invalidate([qk.clip(id!)]),
    onError: (e) => toast.push({ kind: 'err', title: 'Regenerare indisponibilă', body: humanError(e) }),
  });
  const acceptMut = useMutation({
    mutationFn: () => acceptClip(id!),
    onSuccess: () => {
      invalidate([qk.clip(id!), ['clips']]);
      toast.push({ kind: 'ok', title: 'Clip livrat!', body: 'Îl găsești în Livrări.' });
    },
    onError: (e) => toast.push({ kind: 'err', title: 'Eroare', body: humanError(e) }),
  });
  const ultraBriefMut = useMutation({
    mutationFn: () => submitUltraBrief(id!, { brief: briefText.trim() }),
    onSuccess: (job) => {
      invalidate([qk.clip(id!)]);
      toast.push({ kind: 'ok', title: 'Brief trimis editorului' });
      navigate(`/ultra/${job.id}`);
    },
    onError: (e) => toast.push({ kind: 'err', title: 'Eroare', body: humanError(e) }),
  });

  if (clipQ.isLoading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <Skeleton height={30} width="40%" />
        <Skeleton height={220} />
      </div>
    );
  }
  if (clipQ.isError || !c || !data) {
    return <ErrorBlock message="Nu am găsit acest clip." onRetry={() => clipQ.refetch()} />;
  }

  const scenario = data.scenario;
  const imageFileIds = data.steps
    .filter((s) => s.phase === 'images' && s.type === 'image_generate')
    .flatMap((s) => s.outputFileIds);
  const counterName = counterLabel(`${c.category}_${c.tier}`);
  const activeFlow = FLOW.findIndex((f) => f.states.includes(c.status));

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
        <Link to="/livrari" style={{ fontSize: 13, fontWeight: 800, color: 'var(--muted)', textDecoration: 'none' }}>
          ← Clipurile mele
        </Link>
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <h1 className="bricolage" style={{ fontWeight: 800, fontSize: 27 }}>{c.title || 'Clip fără titlu'}</h1>
          <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
            <ClipStatusBadge status={c.status} />
            <span className="pill" style={{ background: 'var(--bg2)', color: 'var(--muted)' }}>
              {categoryLabel(c.category)} · {tierLabel(c.tier)} · {c.format}
            </span>
          </div>
        </div>
      </div>

      {/* Stepper */}
      <div style={{ display: 'flex', gap: 6, margin: '20px 0 24px', flexWrap: 'wrap' }}>
        {FLOW.map((f, i) => (
          <div key={f.key} style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1, minWidth: 90 }}>
            <div style={{ flex: 1 }}>
              <div style={{ height: 5, borderRadius: 3, background: i <= activeFlow ? 'var(--accent)' : 'var(--bg2)' }} />
              <span style={{ fontSize: 11, fontWeight: 800, color: i === activeFlow ? 'var(--ink)' : 'var(--muted)', marginTop: 4, display: 'block' }}>
                {f.label}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* ==== Conținut pe stare ==== */}
      {isProcessing(c.status) && <ProcessingCard clip={c} />}

      {c.status === 'failed' && (
        <ErrorBlock
          message={c.error || 'A apărut o eroare tehnică la generare. Reîncercarea este gratuită și nu consumă regenerări.'}
          onRetry={() => {
            const step = c.consumed ? 'video' : c.previewFileId ? 'video' : 'scenario';
            regenMut.mutate(step as 'scenario' | 'images' | 'video');
          }}
        />
      )}

      {c.status === 'scenariu_generat' && scenario && (
        <ScenarioApproval
          clipId={id!}
          segments={scenario.segments}
          onApprove={() => approveScriptMut.mutate()}
          approving={approveScriptMut.isPending}
          onRegen={() => regenMut.mutate('scenario')}
          regenerating={regenMut.isPending}
        />
      )}

      {c.status === 'imagini_generate' && (
        <ImageApproval
          imageFileIds={imageFileIds}
          format={c.format}
          counterName={counterName}
          onApprove={() => approveImagesMut.mutate()}
          approving={approveImagesMut.isPending}
          onRegen={() => regenMut.mutate('images')}
          regenerating={regenMut.isPending}
        />
      )}

      {c.status === 'preview' && (
        <PreviewCard
          clipId={id!}
          format={c.format}
          scenario={scenario}
          regenUsed={c.regenUsed}
          regenLimit={c.regenLimit}
          isUltra={c.tier === 'ultra'}
          onAccept={() => acceptMut.mutate()}
          accepting={acceptMut.isPending}
          onRegen={() => regenMut.mutate('video')}
          regenerating={regenMut.isPending}
          briefText={briefText}
          setBriefText={setBriefText}
          onUltraBrief={() => ultraBriefMut.mutate()}
          ultraBriefing={ultraBriefMut.isPending}
        />
      )}

      {(c.status === 'ultra_briefing' || c.status === 'ultra_editare' || c.status === 'ultra_review') && (
        <div className="card" style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <p style={{ margin: 0, fontWeight: 800 }}>Acest clip Ultra e la editor.</p>
          <p style={{ margin: 0, fontSize: 13.5, color: 'var(--muted)' }}>Urmărește progresul, mesajele și review-ul final în Comenzi Ultra.</p>
          <Link to="/ultra" className="btn btn-primary btn-sm" style={{ alignSelf: 'flex-start' }}>Deschide Comenzi Ultra →</Link>
        </div>
      )}

      {c.status === 'livrat' && (
        <DeliveredCard clipId={id!} format={c.format} onShare={() => setShareOpen(true)} />
      )}

      <ShareModal clipId={id!} open={shareOpen} onClose={() => setShareOpen(false)} />
    </div>
  );
}

function ProcessingCard({ clip }: { clip: { status: ClipStatus; progress: number } }) {
  const pct = clip.progress ?? 0;
  const eta = Math.max(1, Math.round((100 - pct) / 12));
  return (
    <div className="card" data-testid="processing" style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <Spinner size={22} />
        <div>
          <p style={{ margin: 0, fontWeight: 800, fontSize: 16 }}>{CLIP_STATUS_LABEL[clip.status]}</p>
          <p style={{ margin: '2px 0 0', fontSize: 12.5, color: 'var(--muted)' }}>Poți pleca — te anunțăm pe email când e gata. ~{eta} min rămase.</p>
        </div>
        <span className="bricolage tnum" style={{ marginLeft: 'auto', fontWeight: 800, fontSize: 22 }}>{pct}%</span>
      </div>
      <div style={{ height: 6, borderRadius: 4, background: 'var(--bg2)', overflow: 'hidden' }}>
        <span style={{ display: 'block', height: '100%', borderRadius: 4, background: 'var(--accent)', width: `${pct}%`, transition: 'width 1s ease' }} />
      </div>
    </div>
  );
}

function ScenarioApproval({
  clipId,
  segments,
  onApprove,
  approving,
  onRegen,
  regenerating,
}: {
  clipId: string;
  segments: ScenarioSegment[];
  onApprove: () => void;
  approving: boolean;
  onRegen: () => void;
  regenerating: boolean;
}) {
  const toast = useToast();
  const invalidate = useInvalidate();
  const [drafts, setDrafts] = useState<Record<number, string>>({});
  const [pron, setPron] = useState<Record<number, string>>({});
  const [savedIdx, setSavedIdx] = useState<number | null>(null);
  const kindLabel: Record<string, string> = { hook: 'HOOK', content: 'CONȚINUT', cta: 'CTA' };

  // Constatare (a): persistă textul segmentului editat (doar înainte de aprobare).
  const editMut = useMutation({
    mutationFn: ({ index, text }: { index: number; text: string }) => editSegment(clipId, index, text),
    onSuccess: (_d, v) => {
      setSavedIdx(v.index);
      setTimeout(() => setSavedIdx((i) => (i === v.index ? null : i)), 1800);
      invalidate([qk.clip(clipId)]);
    },
    onError: (e) => toast.push({ kind: 'err', title: 'Nu am putut salva', body: humanError(e) }),
  });
  // Constatare (c): setează respelling-ul pe segment înainte de generarea audio (fără regen).
  const pronMut = useMutation({
    mutationFn: ({ index, respelling }: { index: number; respelling: string }) =>
      setPronunciation(clipId, { segmentIndex: index, respelling }),
    onSuccess: () => {
      toast.push({ kind: 'ok', title: 'Pronunție salvată', body: 'Se aplică la generarea vocii.' });
      invalidate([qk.clip(clipId)]);
    },
    onError: (e) => toast.push({ kind: 'err', title: 'Nu am putut seta pronunția', body: humanError(e) }),
  });

  const persistSegment = (s: ScenarioSegment) => {
    const draft = drafts[s.index];
    if (draft !== undefined && draft.trim() && draft !== s.text) {
      editMut.mutate({ index: s.index, text: draft });
    }
  };

  return (
    <div data-testid="scenario-approval" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div className="pill" style={{ alignSelf: 'flex-start', background: 'var(--ok-soft)', color: 'var(--ok)' }}>
        ✓ Iterezi gratuit — nimic nu se consumă din sold la acest pas.
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {segments.map((s) => (
          <div key={s.index} className="card" style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span className="mono" style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '.08em', color: 'var(--accent-text)' }}>
                {kindLabel[s.kind] ?? s.kind.toUpperCase()}
              </span>
              {savedIdx === s.index && (
                <span style={{ fontSize: 10.5, color: 'var(--ok)', fontWeight: 700 }}>✓ salvat</span>
              )}
              <span className="tnum" style={{ fontSize: 11, color: 'var(--muted)', marginLeft: 'auto' }}>
                {s.startS}s – {s.endS}s
              </span>
            </div>
            <textarea
              className="input"
              data-testid={`segment-${s.index}`}
              rows={2}
              value={drafts[s.index] ?? s.text}
              onChange={(e) => setDrafts((d) => ({ ...d, [s.index]: e.target.value }))}
              onBlur={() => persistSegment(s)}
              style={{ fontWeight: 600, fontSize: 14 }}
            />
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              <input
                className="input"
                data-testid={`pronunciation-${s.index}`}
                placeholder='Pronunție (opțional) — ex. „ka-fea"'
                value={pron[s.index] ?? s.pronunciation ?? ''}
                onChange={(e) => setPron((p) => ({ ...p, [s.index]: e.target.value }))}
                style={{ fontSize: 12.5, flex: 1, minWidth: 160 }}
              />
              <button
                className="btn btn-ghost btn-sm"
                disabled={pronMut.isPending || !(pron[s.index] ?? '').trim()}
                onClick={() => pronMut.mutate({ index: s.index, respelling: (pron[s.index] ?? '').trim() })}
              >
                Setează pronunția
              </button>
            </div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <button className="btn btn-ghost btn-sm" onClick={onRegen} disabled={regenerating}>
          {regenerating ? <Spinner /> : '↺ Regenerează scenariul (gratuit)'}
        </button>
        <span style={{ fontSize: 12, color: 'var(--muted)', alignSelf: 'center' }}>
          Editările de text se salvează automat. Pronunția setată aici se aplică la generarea vocii; o poți ajusta și la preview.
        </span>
      </div>

      <div className="card" style={{ padding: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12.5, color: 'var(--muted)' }}>după aprobare generăm imaginile — tot gratuit</span>
        <button className="btn btn-primary" data-testid="approve-script" onClick={onApprove} disabled={approving}>
          {approving ? <Spinner /> : 'Aprobă scenariul →'}
        </button>
      </div>
    </div>
  );
}

function ImageApproval({
  imageFileIds,
  format,
  counterName,
  onApprove,
  approving,
  onRegen,
  regenerating,
}: {
  imageFileIds: string[];
  format: string;
  counterName: string;
  onApprove: () => void;
  approving: boolean;
  onRegen: () => void;
  regenerating: boolean;
}) {
  return (
    <div data-testid="image-approval" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <p style={{ margin: 0, fontSize: 13.5, color: 'var(--muted)' }}>
        Verifică imaginea fiecărei scene. Regenerezi individual, gratuit. Abia butonul de mai jos pornește pasul care consumă din sold.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(130px,1fr))', gap: 12 }}>
        {imageFileIds.map((fid) => (
          <div key={fid} style={{ aspectRatio: format.replace(':', '/'), borderRadius: 12, overflow: 'hidden', border: '1px solid var(--line)', background: 'var(--bg2)' }}>
            <MediaImage fileId={fid} alt="Imagine scenă" />
          </div>
        ))}
        {imageFileIds.length === 0 && <Skeleton height={180} />}
      </div>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <button className="btn btn-ghost btn-sm" onClick={onRegen} disabled={regenerating}>
          {regenerating ? <Spinner /> : '↺ Regenerează imaginile (gratuit)'}
        </button>
      </div>

      <div className="card" style={{ padding: 18, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', borderColor: 'var(--warn)' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span className="pill" style={{ alignSelf: 'flex-start', background: 'var(--warn-soft)', color: 'var(--warn)' }} data-testid="consume-warning">
            Acest pas consumă 1 clip din soldul „{counterName}"
          </span>
          <span style={{ fontSize: 12.5, color: 'var(--muted)' }}>Restul pașilor (video, voce, montaj) sunt incluși.</span>
        </div>
        <button className="btn btn-primary" data-testid="approve-images" onClick={onApprove} disabled={approving}>
          {approving ? <Spinner /> : 'Aprobă și generează videoclipul'}
        </button>
      </div>
    </div>
  );
}

function PreviewCard({
  clipId,
  format,
  scenario,
  regenUsed,
  regenLimit,
  isUltra,
  onAccept,
  accepting,
  onRegen,
  regenerating,
  briefText,
  setBriefText,
  onUltraBrief,
  ultraBriefing,
}: {
  clipId: string;
  format: string;
  scenario: { segments: ScenarioSegment[] } | null;
  regenUsed: number;
  regenLimit: number;
  isUltra: boolean;
  onAccept: () => void;
  accepting: boolean;
  onRegen: () => void;
  regenerating: boolean;
  briefText: string;
  setBriefText: (v: string) => void;
  onUltraBrief: () => void;
  ultraBriefing: boolean;
}) {
  const [showPron, setShowPron] = useState(false);
  const preview = useQuery({ queryKey: ['clip-preview', clipId], queryFn: () => downloadClip(clipId, 'preview') });
  const overLimit = regenUsed >= regenLimit;

  return (
    <div data-testid="preview" style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 320px) 1fr', gap: 24, alignItems: 'start' }} className="dash-grid">
      <ClipFrame format={format as never} watermark maxWidth={320}>
        {preview.data?.url ? (
          <MediaVideo url={preview.data.url} />
        ) : (
          <span className="mono" style={{ color: 'rgba(255,255,255,.5)', fontSize: 11 }}>[preview]</span>
        )}
      </ClipFrame>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div>
          <span className="pill" style={{ background: 'var(--warn-soft)', color: 'var(--warn)' }}>Preview cu watermark</span>
          <p style={{ margin: '10px 0 0', fontSize: 13.5, color: 'var(--muted)' }}>
            Uită-te pe preview cu tot cu subtitrări. Descarci doar dacă îți place — până atunci nimic nu e bătut în cuie.
          </p>
        </div>

        {!isUltra ? (
          <>
            <button className="btn btn-primary" data-testid="accept-clip" onClick={onAccept} disabled={accepting}>
              {accepting ? <Spinner /> : 'Acceptă și descarcă (fără watermark)'}
            </button>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
              <button className="btn btn-ghost btn-sm" data-testid="regen-video" onClick={onRegen} disabled={regenerating}>
                {regenerating ? <Spinner /> : '↺ Regenerează'}
              </button>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowPron((v) => !v)}>Corectează pronunția</button>
              <span className="tnum" data-testid="regen-counter" style={{ fontSize: 12, color: overLimit ? 'var(--warn)' : 'var(--muted)' }}>
                {overLimit
                  ? `Ai folosit toate cele ${regenLimit} regenerări gratuite — următoarea se plătește.`
                  : `Regenerări gratuite: ${regenUsed}/${regenLimit}`}
              </span>
            </div>
            {showPron && scenario && <PronunciationAssistant clipId={clipId} segments={scenario.segments} />}
          </>
        ) : (
          <div className="card" style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <p style={{ margin: 0, fontWeight: 800 }}>Ultra: trimite la editorul uman</p>
            <p style={{ margin: 0, fontSize: 12.5, color: 'var(--muted)' }}>
              Scrie-i editorului ce vrei la montaj (ritm, text pe ecran, accente). Livrare în 12–48h.
            </p>
            <textarea
              className="input"
              data-testid="ultra-brief"
              rows={3}
              placeholder="Ex. taie primele 2 secunde, pune logo la final, subtitrări mari"
              value={briefText}
              onChange={(e) => setBriefText(e.target.value)}
            />
            <button className="btn btn-primary btn-sm" data-testid="submit-ultra-brief" onClick={onUltraBrief} disabled={ultraBriefing || !briefText.trim()} style={{ alignSelf: 'flex-start' }}>
              {ultraBriefing ? <Spinner /> : 'Trimite brief-ul editorului →'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function DeliveredCard({ clipId, format, onShare }: { clipId: string; format: string; onShare: () => void }) {
  const finalUrl = useQuery({ queryKey: ['clip-final', clipId], queryFn: () => downloadClip(clipId, 'final') });
  return (
    <div data-testid="delivered" style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 320px) 1fr', gap: 24, alignItems: 'start' }} className="dash-grid">
      <ClipFrame format={format as never} maxWidth={320}>
        {finalUrl.data?.url ? <MediaVideo url={finalUrl.data.url} /> : <span className="mono" style={{ color: 'rgba(255,255,255,.5)', fontSize: 11 }}>[clip final]</span>}
      </ClipFrame>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <span className="pill" style={{ alignSelf: 'flex-start', background: 'var(--ok-soft)', color: 'var(--ok)' }}>
          ✓ Livrat — fără watermark
        </span>
        <p style={{ margin: 0, fontSize: 13.5, color: 'var(--muted)' }}>Clipul e gata de postat. Îl descarci sau creezi un link public de vizionare.</p>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <a className="btn btn-primary" data-testid="download-final" href={resolveMediaUrl(finalUrl.data?.url) ?? '#'} onClick={(e) => { if (!finalUrl.data?.url) e.preventDefault(); }} download target="_blank" rel="noreferrer">
            ↓ Descarcă MP4
          </a>
          <button className="btn btn-ghost" data-testid="open-share" onClick={onShare}>
            Share — link public
          </button>
        </div>
      </div>
    </div>
  );
}
